import React, { useState } from "react";
import {
  Badge,
  Box,
  Button,
  ExpandableSection,
  SpaceBetween,
  StatusIndicator,
} from "@amzn/awsui-components-react";
import { InvestigationFieldBreakdown } from "../../lusca-search-api/generated-src";
import {
  SplitSuggestion,
  SplitVerdict,
  suggestSplits,
} from "./investigationUtils";

interface SuggestedSplitsProps {
  fieldBreakdown?: InvestigationFieldBreakdown[];
  /** Population size the breakdown was computed over (for the header). */
  totalCount?: number;
  /**
   * Manifest cap. When the population exceeds it, the section leads with the values that would
   * bring it under the cap and disables the ones that would not.
   */
  maxPopulation?: number;
  /**
   * Value of each unpinned field on the DLQ item the rule was seeded from. Shown as "Your item"
   * per row and its chip is listed first. Omit a field (or pass an empty
   * object) when there is no such item, e.g. a hand-typed rule.
   */
  seedValues: Record<string, string | undefined>;
  /** Tick the field and set its value in the population rule. */
  onApply: (field: string, value: string) => void;
  /** Open by default when the population must be narrowed (e.g. it is over the manifest cap). */
  defaultExpanded?: boolean;
}

/** Chips shown per field before the rest fold behind "Show N more". */
export const MAX_VISIBLE_CHIPS = 3;

const VERDICT_LABEL: Record<SplitVerdict, string> = {
  split: "Splits well",
  outlier: "Isolates an outlier",
  fragmented: "Too fragmented",
  uniform: "Adds nothing",
};

const VERDICT_TYPE: Record<
  SplitVerdict,
  "success" | "info" | "warning" | "stopped"
> = {
  split: "success",
  outlier: "info",
  fragmented: "warning",
  uniform: "stopped",
};

const percent = (share: number) => {
  if (share >= 0.995 && share < 1) return ">99%";
  if (share > 0 && share < 0.005) return "<1%";
  return `${Math.round(share * 100)}%`;
};

const formatCount = (n: number) => n.toLocaleString("en-US");

const NO_VALUE = "no value";

const sameValue = (
  suggestion: SplitSuggestion,
  a: string | undefined,
  b: string
) =>
  a !== undefined &&
  (suggestion.boolean ? a.toLowerCase() === b.toLowerCase() : a === b);

interface Chip {
  value: string;
  count?: number;
  share?: number;
  isSeed: boolean;
}

/**
 * Chips to offer, in count order (the seed's value is appended if it fell outside the reported top
 * values). Chips are deliberately undecorated — a check or plus on a button reads as "selected" /
 * "add"; the "Your item" line names the seed's value when it is not simply the first chip.
 */
function chipsFor(suggestion: SplitSuggestion, seedValue?: string): Chip[] {
  const chips: Chip[] = suggestion.values
    .filter((v) => v.value !== "") // an empty value cannot become a predicate
    .map((v) => ({ ...v, isSeed: sameValue(suggestion, seedValue, v.value) }));
  const seedShown = chips.some((c) => c.isSeed);
  if (!seedShown && seedValue && suggestion.otherCount > 0) {
    chips.push({ value: seedValue, isSeed: true });
  }
  return chips;
}

/** Whether pinning this chip brings the population under the cap (always true without a cap). */
const fits = (chip: Chip, maxPopulation?: number) =>
  maxPopulation === undefined ||
  chip.count === undefined ||
  chip.count <= maxPopulation;

const chipStats = (chip: Chip) =>
  chip.count !== undefined && chip.share !== undefined
    ? ` · ${formatCount(chip.count)} (${percent(chip.share)})`
    : "";

const SplitRow: React.FC<{
  suggestion: SplitSuggestion;
  seedValue?: string;
  maxPopulation?: number;
  onApply: (field: string, value: string) => void;
}> = ({ suggestion, seedValue, maxPopulation, onApply }) => {
  const [showAll, setShowAll] = useState(false);
  const hasSeed = seedValue !== undefined;
  const seedLabel = seedValue === "" ? NO_VALUE : seedValue;

  const chips = chipsFor(suggestion, seedValue);
  // The seed's chip stays visible even when it ranks below the fold.
  const visible = showAll
    ? chips
    : chips.filter((c, i) => i < MAX_VISIBLE_CHIPS || c.isSeed);
  const folded = chips.length - visible.length;
  const seedInOther =
    hasSeed &&
    seedValue !== "" &&
    !suggestion.values.some((v) => sameValue(suggestion, seedValue, v.value));
  const emptyCount = suggestion.values.find((v) => v.value === "")?.count ?? 0;
  const hiddenValues = suggestion.distinctValues - suggestion.values.length;

  return (
    <div
      data-testid={`split-${suggestion.field}`}
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(180px, 220px) 150px 1fr",
        gap: "8px",
        alignItems: "start",
      }}
    >
      <strong>{suggestion.label}</strong>
      <StatusIndicator type={VERDICT_TYPE[suggestion.verdict]}>
        {VERDICT_LABEL[suggestion.verdict]}
      </StatusIndicator>
      <SpaceBetween size="xxs">
        {hasSeed && !chips[0]?.isSeed && (
          <div data-testid={`your-item-${suggestion.field}`}>
            <Box variant="span" color="text-body-secondary">
              Your item:{" "}
            </Box>
            <strong>{seedLabel}</strong>
            {seedInOther && (
              <Box variant="span" color="text-body-secondary">
                {" "}
                (among the {formatCount(suggestion.otherCount)} items in other
                values)
              </Box>
            )}
          </div>
        )}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "6px",
            alignItems: "center",
          }}
        >
          {visible.map((chip) => {
            const stats = chipStats(chip);
            const ok = fits(chip, maxPopulation);
            const aria = chip.isSeed
              ? `Narrow to ${suggestion.label} = ${chip.value} — keeps the item you started from${stats}`
              : `Narrow to ${suggestion.label} = ${chip.value}${stats}`;
            return (
              <Button
                key={chip.value}
                variant="normal"
                ariaLabel={ok ? aria : `${aria} — still over the limit`}
                disabled={!ok}
                disabledReason={
                  ok
                    ? undefined
                    : `Still ${formatCount(
                        chip.count ?? 0
                      )} items — over the ${formatCount(
                        maxPopulation ?? 0
                      )} limit`
                }
                onClick={() => onApply(suggestion.field, chip.value)}
              >
                {chip.value}
                {stats}
              </Button>
            );
          })}
          {folded > 0 && (
            <Button
              variant="inline-link"
              ariaLabel={`Show ${folded} more ${suggestion.label} values`}
              onClick={() => setShowAll(true)}
            >
              Show {folded} more
            </Button>
          )}
          {(showAll || folded === 0) &&
            (hiddenValues > 0 || emptyCount > 0) && (
              <Badge color="grey">
                {hiddenValues > 0 &&
                  `+${formatCount(
                    suggestion.otherCount
                  )} items in ${hiddenValues} other ${
                    hiddenValues === 1 ? "value" : "values"
                  }`}
                {hiddenValues > 0 && emptyCount > 0 && " · "}
                {emptyCount > 0 && `${formatCount(emptyCount)} with no value`}
              </Badge>
            )}
        </div>
      </SpaceBetween>
    </div>
  );
};

/**
 * The one-line answer when the population is over the cap: the best-ranked field with at least one
 * value that fits, and up to two of those values.
 */
const Recommendation: React.FC<{
  suggestions: SplitSuggestion[];
  seedValues: Record<string, string | undefined>;
  maxPopulation: number;
  onApply: (field: string, value: string) => void;
}> = ({ suggestions, seedValues, maxPopulation, onApply }) => {
  const best = suggestions
    .filter((s) => s.verdict !== "uniform")
    .map((suggestion) => ({
      suggestion,
      chips: chipsFor(suggestion, seedValues[suggestion.field]).filter(
        (c) => c.count !== undefined && fits(c, maxPopulation)
      ),
    }))
    .find(({ chips }) => chips.length > 0);
  const recommended = best?.chips.slice(0, 2) ?? [];
  return (
    <div
      data-testid="split-recommendation"
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "6px",
        alignItems: "center",
      }}
    >
      {best ? (
        <>
          <Box variant="span">
            To get under {formatCount(maxPopulation)}, add{" "}
            <strong>{best.suggestion.label}</strong> ={" "}
          </Box>
          {recommended.map((chip, i) => (
            <React.Fragment key={chip.value}>
              {i > 0 && <Box variant="span">or</Box>}
              <Button
                variant="primary"
                ariaLabel={`Add ${best.suggestion.label} = ${
                  chip.value
                }${chipStats(chip)}`}
                onClick={() => onApply(best.suggestion.field, chip.value)}
              >
                {chip.value}
                {chipStats(chip)}
              </Button>
            </React.Fragment>
          ))}
        </>
      ) : (
        <Box variant="span">
          No single field brings this under {formatCount(maxPopulation)} — add
          one below, then narrow again.
        </Box>
      )}
    </div>
  );
};

/**
 * "Narrow this population": for every rule field not yet pinned, how the previewed population is
 * spread across its values, ranked by how well the field would divide it. Each row names the
 * value the seed DLQ item has, and clicking any value adds `field = value` to the rule. Fields
 * with a single value are omitted — they cannot narrow anything.
 */
export const SuggestedSplits: React.FC<SuggestedSplitsProps> = ({
  fieldBreakdown,
  totalCount,
  maxPopulation,
  seedValues,
  onApply,
  defaultExpanded = false,
}) => {
  const suggestions = suggestSplits(fieldBreakdown);
  // Fields with a single value can't narrow anything, so they are simply not shown.
  const useful = suggestions.filter((s) => s.verdict !== "uniform");
  // Nothing to click on when every field has a single value — don't show an empty panel.
  if (!useful.length) return null;
  const total = totalCount ?? suggestions[0]?.totalCount ?? 0;
  const overCap = maxPopulation !== undefined && total > maxPopulation;
  return (
    <ExpandableSection
      variant="footer"
      defaultExpanded={defaultExpanded}
      headerText={`Narrow this population (${useful.length} of ${suggestions.length} fields could narrow it)`}
      headerDescription={`Click a value to add it to the rule. Counts are over the ${formatCount(
        total
      )} matching items.`}
    >
      <SpaceBetween size="s">
        {overCap && (
          <Recommendation
            suggestions={suggestions}
            seedValues={seedValues}
            maxPopulation={maxPopulation}
            onApply={onApply}
          />
        )}
        {useful.map((s) => (
          <SplitRow
            key={s.field}
            suggestion={s}
            seedValue={seedValues[s.field]}
            maxPopulation={overCap ? maxPopulation : undefined}
            onApply={onApply}
          />
        ))}
      </SpaceBetween>
    </ExpandableSection>
  );
};

export default SuggestedSplits;
